/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC } from "react";
import { TemplateId } from "src/globalTypes";
import {
  columnWidthCss,
  getTableCssForDetailVariant,
  tableCssForMobileTableVariant,
} from "./styles";

import CellEntity from "./CellEntity";
import { MobileRowLayout } from "../../../../ColumnTemplatePositionEditor/types";
import { TableColumns, TableRow } from "../../../../../../requests/table/types";
import { ColumnFormatDict } from "../../../../../../requests/templates/types";
import Loader, {
  WithLoaderHOC,
  withLoaderHOC,
} from "../../../../../atoms/Loader";
import { TableDataCellsVariant } from "./types";
import useViewportDimensions from "../../../../../../hooks/viewport/useViewportDimensions";
import { UpdateTableRowCell } from "../../../TableDataDesktop/TableDataRow/TableDataRow";
import { useFormatMsg } from "../../../../../../hooks/useFormatMsg";
import {
  generateLayoutBlueprint,
  removeOverflownFromBlueprint,
} from "../../../../../../utils/mobileLayout";
import { getDetailTableId } from "./utils";

const Cells: FC<Props> = ({
  positionData,
  tableRowData,
  columns,
  onCellEdit,
  onCellBlur,
  tableRowCallback,
  columnFormatMobileDict,
  editMode,
  className,
  variant,
  templateId,
}) => {
  const screenWidth = useViewportDimensions().x;
  const formatMsg = useFormatMsg();

  if (!positionData) return <Loader size={30} />;
  const { width, cellEntities, delimiters } = positionData;

  if (!cellEntities?.length) {
    const isDetail = variant === "detail";
    const msgId = isDetail
      ? "table.no_position_data.detail"
      : "table.no_position_data.mobile_table";

    return <div>{formatMsg(msgId)}</div>;
  }

  const tableCss =
    variant === "table"
      ? tableCssForMobileTableVariant
      : getTableCssForDetailVariant(width, screenWidth);

  const blueprint = generateLayoutBlueprint(positionData);
  const filteredBlueprint = removeOverflownFromBlueprint(blueprint);

  return (
    <table css={tableCss} className={className}>
      <tbody id={getDetailTableId(templateId)}>
        {filteredBlueprint.map((row, index) => (
          <tr data-cy="tr-table-row" key={`${String(index)}-row`}>
            {row.map((cell, indexRow) => {
              if (cell === "EMPTY") {
                return (
                  <td
                    key={`${String(indexRow)}-EMPTY`}
                    css={columnWidthCss(row.length, 1)}
                  />
                );
              }

              const { x, colspan, rowspan } = cell;

              return (
                <td
                  data-cy="tr-table-content"
                  css={columnWidthCss(row.length, colspan)}
                  colSpan={colspan}
                  rowSpan={rowspan}
                  key={x}
                >
                  <CellEntity
                    entity={cell}
                    columns={columns}
                    delimiters={delimiters}
                    row={tableRowData}
                    formatDict={columnFormatMobileDict}
                    onCellEdit={onCellEdit}
                    onCellBlur={onCellBlur}
                    editMode={editMode}
                    tableRowCallback={tableRowCallback}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

interface Props {
  positionData?: MobileRowLayout;
  tableRowData: TableRow;
  columns: TableColumns;
  columnFormatMobileDict: ColumnFormatDict;
  editMode?: boolean;
  onCellEdit?: UpdateTableRowCell;
  onCellBlur?(): void;
  tableRowCallback?(): void;
  className?: string;
  variant: TableDataCellsVariant;
  templateId?: TemplateId | null;
}

export default withLoaderHOC(Cells);
export type CellsProps = WithLoaderHOC<Props>;
